import classNames from 'classnames';
import { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import {
  getSubscriptionPrice,
  LEGACY_PREMIUM_PLAN_1,
  LEGACY_PREMIUM_PLAN_2,
  PREMIUM_COMP_SEC_PLAN_NAME,
  PREMIUM_COMPLIANCE_PLAN_NAME,
} from '../../../../shared/subscriptions';
import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import InternalToolingStore from '../../../redux/internal_tooling';
import Flex from '../../generic/Flex';
import TextInput from '../../generic/Forms/TextInput';
import Toggle from '../../generic/Toggle';
import type { ReduxAction } from '../../../types';

import FilteredOrgTable from './FilteredOrgTable';
import OrganizationActions from './OrganizationActions';
import RangedToggle from './RangedToggle';

type OrganizationSearchProps = {
  searchOnly?: boolean;
};

type AndOrSwitchProps = {
  andOrToggle: boolean;
};

type ButtonToggleProps = {
  text: string;
  enabled: boolean;
  onClickActionCreator: (e: unknown) => Pick<ReduxAction, 'type'>;
};

type FilterTogglesProps = {
  isFreeFilterEnabled: boolean;
  isPremiumFilterEnabled: boolean;
  isPremiumSeatFilterEnabled: boolean;
  isPremiumSeat2FilterEnabled: boolean;
  isPremiumComplianceFilterEnabled: boolean;
  isPremiumCompSecFilterEnabled: boolean;
  isCustomBillingFilterEnabled: boolean;
  contributorCountRange: { min: number; max: number };
  isContributorCountFilterEnabled: boolean;
  projectCountRange: { min: number; max: number };
  isProjectCountFilterEnabled: boolean;
};

const AndOrSwitch: VoidFunctionComponent<AndOrSwitchProps> = ({
  andOrToggle,
}) => {
  const dispatch = useDispatch();

  return (
    <Flex grow={0.25} align="center">
      <p>OR</p>&nbsp;
      <Toggle
        checked={andOrToggle}
        onChange={() =>
          dispatch(InternalToolingStore.actions.toggleAndOrFilter())}
        icons={false}
      />
      &nbsp;<p>AND</p>
    </Flex>
  );
};

const ButtonToggle: VoidFunctionComponent<ButtonToggleProps> = ({
  text,
  enabled,
  onClickActionCreator,
}) => {
  const dispatch = useDispatch();
  const buttonClassName = classNames({
    filter: true,
    selected: enabled,
  });

  return (
    <Button
      bsStyle={buttonClassName}
      onClick={(e) => dispatch(onClickActionCreator(e))}
      style={{ marginRight: 15 }}
    >
      {text}
    </Button>
  );
};

const FilterToggles: VoidFunctionComponent<FilterTogglesProps> = (props) => {
  return (
    <div className="wrapped-row">
      <ButtonToggle
        text="free"
        enabled={props.isFreeFilterEnabled}
        onClickActionCreator={InternalToolingStore.actions.toggleFreeFilter}
      />
      <ButtonToggle
        text="premium"
        enabled={props.isPremiumFilterEnabled}
        onClickActionCreator={InternalToolingStore.actions.togglePremiumFilter}
      />
      <ButtonToggle
        text={`$${getSubscriptionPrice(LEGACY_PREMIUM_PLAN_1)}/dev * month (legacy)`}
        enabled={props.isPremiumSeatFilterEnabled}
        onClickActionCreator={
          InternalToolingStore.actions.togglePremiumSeatFilter
        }
      />
      <ButtonToggle
        text={`$${getSubscriptionPrice(LEGACY_PREMIUM_PLAN_2)}/dev * month (legacy)`}
        enabled={props.isPremiumSeat2FilterEnabled}
        onClickActionCreator={
          InternalToolingStore.actions.togglePremiumSeat2Filter
        }
      />
      <ButtonToggle
        text={`$${getSubscriptionPrice(PREMIUM_COMPLIANCE_PLAN_NAME)}/dev * month`}
        enabled={props.isPremiumComplianceFilterEnabled}
        onClickActionCreator={
          InternalToolingStore.actions.togglePremiumComplianceFilter
        }
      />
      <ButtonToggle
        text={`$${getSubscriptionPrice(PREMIUM_COMP_SEC_PLAN_NAME)}/dev * month`}
        enabled={props.isPremiumCompSecFilterEnabled}
        onClickActionCreator={
          InternalToolingStore.actions.togglePremiumCompSecFilter
        }
      />
      <ButtonToggle
        text="Custom Billing"
        enabled={props.isCustomBillingFilterEnabled}
        onClickActionCreator={
          InternalToolingStore.actions.toggleCustomBillingFilter
        }
      />
      <RangedToggle
        text="Contributor Count"
        range={props.contributorCountRange}
        setRange={InternalToolingStore.actions.setContributorCountFilter}
        filterEnabled={props.isContributorCountFilterEnabled}
        toggleFilter={InternalToolingStore.actions.toggleContributorCountFilter}
      />
      <RangedToggle
        text="Projects Count"
        range={props.projectCountRange}
        setRange={InternalToolingStore.actions.setProjectCountFilter}
        filterEnabled={props.isProjectCountFilterEnabled}
        toggleFilter={InternalToolingStore.actions.toggleProjectCountFilter}
      />
    </div>
  );
};

const OrganizationSearch: VoidFunctionComponent<OrganizationSearchProps> = ({
  searchOnly = false,
}) => {
  const dispatch = useDispatch();

  // Redux values
  const andOrToggle = useSelector(
    InternalToolingStore.selectors.getAndOrToggle,
  );
  const orgRequestParams = useSelector(
    InternalToolingStore.selectors.getOrgRequestParams,
  );

  const orgTextFilter = useSelector(InternalToolingStore.selectors.getOrgTextFilter);

  // FilterToggle Redux values. Defining here so that we can use the useEffect to update on change
  // ButtonToggle enabled values
  const isFreeFilterEnabled = useSelector(
    InternalToolingStore.selectors.getFreeFilterToggle,
  );
  const isPremiumFilterEnabled = useSelector(
    InternalToolingStore.selectors.getPremiumFilterToggle,
  );
  const isPremiumSeatFilterEnabled = useSelector(
    InternalToolingStore.selectors.getPremiumSeatFilterToggle,
  );
  const isPremiumSeat2FilterEnabled = useSelector(
    InternalToolingStore.selectors.getPremiumSeat2FilterToggle,
  );
  const isPremiumComplianceFilterEnabled = useSelector(InternalToolingStore.selectors.getPremiumComplianceFilterToggle);
  const isPremiumCompSecFilterEnabled = useSelector(InternalToolingStore.selectors.getPremiumCompSecFilterToggle);
  const isCustomBillingFilterEnabled = useSelector(
    InternalToolingStore.selectors.getCustomBillingFilterToggle,
  );

  // RangedToggle range values and enabled values
  const contributorCountRange = useSelector(
    InternalToolingStore.selectors.getContributorCountRange,
  );
  const isContributorCountFilterEnabled = useSelector(
    InternalToolingStore.selectors.getContributorCountFilterToggle,
  );
  const projectCountRange = useSelector(
    InternalToolingStore.selectors.getProjectCountRange,
  );
  const isProjectCountFilterEnabled = useSelector(
    InternalToolingStore.selectors.getProjectCountFilterToggle,
  );

  const selectedOrg = useSelector(InternalToolingStore.selectors.getSelectedOrg);

  const debouncedFetch = useDebouncedCallback((reqParams) => {
    dispatch(InternalToolingStore.actions.fetchOrgs(reqParams));
  }, 500);

  // For some reason, we lose the last letter of the text input if we do the debounced fetch inside the searchbar callback.
  // Probably because the orgRequestParams selector value isn't updated while inside the function. This effect
  // is will fetch whenever the orgTextFilter to update in the Redux store
  useEffect(() => {
    if (orgTextFilter !== '') {
      debouncedFetch(orgRequestParams);
    }
    // We only want to run this when the orgTextFilter has been updated. orgRequestParams and debouncedFetch update
    // too often for reasons unrelated to the search bar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgTextFilter]);

  return (
    <div className="internal-tool-component">
      <div className="org-title-filter">
        {!searchOnly && <AndOrSwitch andOrToggle={andOrToggle} />}
        <span className="internal-tool-text-input">
          <TextInput
            label="Filter by Title"
            onChange={(value) => {
              dispatch(InternalToolingStore.actions.setOrgTextFilter(value));
            }}
            value={orgTextFilter || ''}
            placeholder="Filter Organization by Title"
          />
        </span>
      </div>
      <FilterToggles
        isFreeFilterEnabled={isFreeFilterEnabled}
        isPremiumFilterEnabled={isPremiumFilterEnabled}
        isPremiumSeatFilterEnabled={isPremiumSeatFilterEnabled}
        isPremiumSeat2FilterEnabled={isPremiumSeat2FilterEnabled}
        isPremiumComplianceFilterEnabled={isPremiumComplianceFilterEnabled}
        isPremiumCompSecFilterEnabled={isPremiumCompSecFilterEnabled}
        isCustomBillingFilterEnabled={isCustomBillingFilterEnabled}
        contributorCountRange={contributorCountRange}
        isContributorCountFilterEnabled={isContributorCountFilterEnabled}
        projectCountRange={projectCountRange}
        isProjectCountFilterEnabled={isProjectCountFilterEnabled}
      />
      <div className="margin-top">
        <small style={{ marginBottom: 15 }}>
          *This may only display up to 1000 orgs. To get the full list, either
          search for the name directly or download a csv with the desired
          filters
        </small>
        <FilteredOrgTable isSearchOnly={searchOnly} />
        {(!searchOnly && selectedOrg) && (
          <OrganizationActions
            selectedOrg={selectedOrg}
            orgRequestParams={orgRequestParams}
          />
        )}
      </div>
    </div>
  );
};

export default OrganizationSearch;
